import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import { useAuth } from "../navigation/AuthenticationStateManager";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { logout } = useAuth();


  // ❌ HIDE HEADER DURING MOCK TEST
  if (location.pathname.startsWith("/candidate/mock-test")) {
    return null;
  }


  /* ---------- NAV ITEMS ---------- */
  const navigationItems = [
    {
      label: "Resume",
      path: "/candidate/resume",
      icon: "FileText",
    },
    {
      label: "Dashboard",
      path: "/candidate/dashboard",
      icon: "LayoutDashboard",
    },
    {
      label: "Preparation",
      path: "/candidate/preparation-guide",
      icon: "book",
    },
    {
      label: "Mock Test",
      path: "/candidate/mock-test/instructions",
      icon: "Brain",
    },
    {
      label: "Company Question",
      path: "/candidate/add-question",
      icon: "HelpCircle",
    },
    {
      label: "Job Hunter",
      path: "/candidate/job-hunter",
      icon: "Jobs",
    },
    {
      label: "Results",
      path: "/candidate/interview-results-hub",
      icon: "BarChart3",
    },
    {
      label: "Profile",
      path: "/candidate/user-profile-settings",
      icon: "User",
    },
  ];

  const isActivePath = (path) =>
    location.pathname === path ||
    location.pathname.startsWith(`${path}/`);

  const handleNavigation = useCallback(
    (path) => {
      console.log("Navigation clicked:", path, "Current location:", location.pathname);
      event.preventDefault();
      navigate(path);
      setIsMobileMenuOpen(false);
    },
    [] // Remove navigate from dependencies
  );

  /* ---------- LOGOUT ---------- */
  const handleLogout = useCallback(() => {
    console.log("Header - Logout button clicked");
    logout();
    setIsMobileMenuOpen(false);
    // Small delay to ensure state updates before navigation
    setTimeout(() => {
      console.log("Header - Navigating to authentication portal");
      navigate("/authentication-portal", { replace: true });
    }, 100);
  }, [logout, navigate]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-[60px] bg-card border-b border-border z-[100] shadow-elevation-1">
      <div className="h-full px-6 flex items-center justify-between">
        {/* LEFT */}
        <div className="flex items-center gap-8">
          <div
            className="flex items-center gap-2 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleNavigation("/candidate/dashboard")}
          >
            <img
              src="/assets/images/Logo_Botervue.png"
              alt="Botervue"
              className="w-[140px] md:w-[180px] h-auto object-contain"
            />
          </div>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-1">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                  ${
                    isActivePath(item.path)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={
                    isActivePath(item.path)
                      ? "var(--color-primary-foreground)"
                      : "currentColor"
                  }
                />
                {item.label}
              </button>
            ))}

            {/* LOGOUT */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Icon name="LogOut" size={18} />
              Logout
            </button>
          </nav>
        </div>

        {/* MOBILE MENU BUTTON */}
        <button
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          className="md:hidden p-2 rounded-md hover:bg-muted"
        >
          <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
        </button>
      </div>

      {/* MOBILE MENU */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-[150]"
            onClick={toggleMobileMenu}
          />
          <div className="fixed top-[60px] right-0 w-64 bg-card border-l border-border shadow-elevation-3 z-[200]">
            <nav className="p-4 flex flex-col gap-2">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium
                    ${
                      isActivePath(item.path)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                >
                  <Icon name={item.icon} size={20} />
                  {item.label}
                </button>
              ))}

              {/* MOBILE LOGOUT */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Icon name="LogOut" size={20} />
                Logout
              </button>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
